import { useTranslation } from "react-i18next";
import { CheckCircle2, AlertCircle, Loader2, Calendar, Sparkles } from "lucide-react";

import { cn } from "@/lib/utils";
import type { AutoBoletoStatus } from "@/services/fiscalService";

export interface AutoBoletoStatusChipProps {
  status: AutoBoletoStatus | undefined;
  className?: string;
}

/**
 * Small inline pill that surfaces the monthly auto-generation state for an IVA DGI obligation.
 * Renders nothing for state='hidden' (past period without history, non-Ultra, non-IVA-DGI).
 */
export function AutoBoletoStatusChip({
  status,
  className,
}: AutoBoletoStatusChipProps): JSX.Element | null {
  const { t, i18n } = useTranslation();
  if (!status || status.state === "hidden") return null;

  const dateFormatter = new Intl.DateTimeFormat(i18n.language || "es", {
    day: "numeric",
    month: "short",
  });
  const formatYmd = (ymd: string): string => {
    const [y, m, d] = ymd.split("-").map(Number);
    if (!y || !m || !d) return ymd;
    return dateFormatter.format(new Date(Date.UTC(y, m - 1, d, 12)));
  };

  let icon: JSX.Element;
  let label: string;
  let tone: "success" | "danger" | "info" | "muted";

  switch (status.state) {
    case "success_auto":
      icon = <Sparkles className="h-3 w-3" />;
      label = t("autoBoleto.successAuto", "Generado por Fiscalo");
      tone = "success";
      break;
    case "success_manual":
      icon = <CheckCircle2 className="h-3 w-3" />;
      label = t("autoBoleto.successManual", "Generado manualmente");
      tone = "success";
      break;
    case "in_progress":
      icon = <Loader2 className="h-3 w-3 animate-spin" />;
      label = t("autoBoleto.inProgress", "Generando automáticamente…");
      tone = "info";
      break;
    case "failed":
      icon = <AlertCircle className="h-3 w-3" />;
      label = t("autoBoleto.failed", "Auto-generación falló");
      tone = "danger";
      break;
    case "scheduled":
      icon = <Calendar className="h-3 w-3" />;
      label = status.nextScheduledDate
        ? t("autoBoleto.scheduledOn", "Auto-generación: {{date}}", {
            date: formatYmd(status.nextScheduledDate),
          })
        : t("autoBoleto.scheduled", "Auto-generación programada");
      tone = "muted";
      break;
    case "disabled":
      icon = <Calendar className="h-3 w-3" />;
      label = t("autoBoleto.disabled", "Auto-generación desactivada");
      tone = "muted";
      break;
    default:
      return null;
  }

  const toneClass =
    tone === "success"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800"
      : tone === "danger"
        ? "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-800"
        : tone === "info"
          ? "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-800"
          : "bg-muted text-muted-foreground border-border";

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium",
        toneClass,
        className,
      )}
      title={status.message ?? undefined}
    >
      {icon}
      {label}
    </span>
  );
}
